/**
 * «منشی هوشمند پارسینا» — one result's window (PM-0080). Render-only: the session
 * is `useSecretary`, the documents and rules are `model.js`.
 *
 * The record button records a command and runs it in the background; its result
 * waits as a notification, and opening that opens THIS — but only when something
 * has to be answered or chosen first (model.routeOf). A booking with nothing
 * missing skips it and opens «ثبت نوبت» itself. Nothing here writes on its own.
 *
 * WHICH patient was meant is the BFF's call, from patientmanagement's ranking of
 * the clinic by sound (PM-0080). Every candidate shows its score and why it matched;
 * «کدام‌یک؟» is answered with one key (1–5, and 0 for «بیمار جدید است» on a booking);
 * a booking for nobody on file goes on as a NEW patient, registered by the form.
 * What the sentence left out is asked on the sheet, one question at a time
 * (QuestionCard) — answered with a tap, a key or a few words.
 *
 * A booking leaves through `onBook` to the existing «ثبت نوبت» form, opened
 * prefilled; «ثبت حضور» and «ثبت پیگیری» are the two writes the
 * sheet commits itself, each with the operation that already existed for it and
 * only on the user's own press. AI-prepared content carries the «پیشنهاد هوش
 * مصنوعی» badge (DESIGN_CONCEPT §13.3); a figure in an answer is the service's.
 *
 * The surface is `AdaptiveModal` — the kit `Dialog` on a desk, `MobileSheet` on a
 * phone — the same one «ثبت نوبت» opens in.
 */
import React from 'react';
import {
  Check, ChevronLeft, CircleHelp, Clock, Lock, Mic, MicOff, MessageSquarePlus, Square, TriangleAlert, User, UserPlus, X,
} from 'lucide-react';
import {
  Alert, Badge, Button, FilterChip, Skeleton, SkeletonRegion, StatusBadge,
} from '@parsina/framework';
import { GRADIENT_BRAND, soft } from '@parsina/framework/tokens';

import { C } from '../constants/theme';
import AdaptiveModal from '../components/common/AdaptiveModal';
import PhoneCandidateList from '../components/common/PhoneCandidateList';
import { usePhoneCandidates } from '../hooks/usePhoneCandidates';
import { useTranslation } from '../i18n';
import { bffMutation } from '../lib/bffClient';
import { LINK_EXISTING_PATIENT } from '../lib/patient';
import { reportAppointmentsChanged } from '../lib/appointmentsSignal';
import { accountUrl } from '../lib/runtimeEnv.js';
import { useCreditPurchase } from '../hooks/useCreditPurchase.js';
import { Kbd, useShortcut } from '../lib/shortcuts.jsx';
import useResponsive from '../hooks/useResponsive.js';
import {
  FOLLOW_UP_KINDS, MATCH_REASONS, NEW_PATIENT_KEY, OUT_OF_SCOPE_TOPICS, PHASE, PICK_KEYS, PRICE_COMMAND,
  PRICE_TRANSCRIPTION, SECRETARY_CREATE_FOLLOW_UP, SECRETARY_MARK_ARRIVED, SKIPPABLE_QUESTIONS, SKIP_KEY,
  bookingHandoff, clockOf, digitGroups, otherCandidates, resultPatient, routeOf,
} from './model';
import { useSecretary } from './useSecretary';

const S = {
  card: {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)', padding: 20,
  },
  label: { fontSize: 12.5, fontWeight: 700, color: C.textSecondary },
  title: { fontSize: 18, fontWeight: 800, color: C.textPrimary, margin: 0 },
  hint: { fontSize: 13.5, color: C.textSecondary, margin: '2px 0 14px' },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', marginTop: 14 },
  disc: {
    width: 44, height: 44, borderRadius: 999, flexShrink: 0,
    display: 'grid', placeItems: 'center',
  },
  ltr: { unicodeBidi: 'isolate', fontVariantNumeric: 'tabular-nums' },
  kbdOnLight: { background: C.bg, border: `1px solid ${C.border}`, color: C.textSecondary },
};

const pick = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%', boxSizing: 'border-box',
  minHeight: 60, marginBottom: 8, padding: '8px 16px', borderRadius: 10,
  border: `1.5px solid ${C.border}`, background: C.card, color: C.textPrimary,
  fontFamily: 'inherit', fontSize: 14, textAlign: 'start', cursor: 'pointer',
};

/**
 * A key cap — on a keyboard. A phone has no F1 and no Ctrl+Enter, and a cap it
 * cannot press is noise on a screen that has room for nothing extra.
 */
function KeyCap(props) {
  const { isMobile } = useResponsive();
  return isMobile ? null : <Kbd {...props} />;
}

/** A verdict with no action: an icon, one line, one explanation. */
function Verdict({ icon, tone = 'neutral', title, children, testid }) {
  const tint = tone === 'warning' ? soft(C.amber, 0.16) : C.bg;
  const ink = tone === 'warning' ? C.amber : C.textSecondary;
  return (
    <div data-testid={testid} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <span aria-hidden="true" style={{ ...S.disc, background: tint, color: ink }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={S.title}>{title}</h3>
        <div style={{ fontSize: 14, color: C.textSecondary, marginTop: 4 }}>{children}</div>
      </div>
    </div>
  );
}

function PatientLine({ patient, t, formatDigits }) {
  const parts = [];
  if (patient.fileNumber) parts.push(t('secretary.choosePatient.file', { file: formatDigits(patient.fileNumber) }));
  if (patient.age != null) parts.push(t('secretary.choosePatient.age', { age: formatDigits(patient.age) }));
  if (patient.phoneTail) parts.push(t('secretary.choosePatient.phoneTail', { tail: formatDigits(patient.phoneTail) }));
  return (
    <span style={{ flex: 1, minWidth: 0 }}>
      <span dir="auto" style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{patient.name}</span>
      {parts.length ? <span style={{ display: 'block', fontSize: 13, color: C.textSecondary }}>{parts.join(' · ')}</span> : null}
    </span>
  );
}

// The BFF's bands (secretary/catalog.js MATCH), for COLOUR only: whether to go on or
// to ask is decided there, and this only tints the bar the desk reads.
const STRONG_MATCH = 90;
const PLAUSIBLE_MATCH = 60;

/** A match score: the number, and a bar that is teal where the secretary would have gone on alone. */
function MatchScore({ score, t, formatDigits }) {
  if (score == null) return null;
  const tone = score >= STRONG_MATCH ? C.teal : score >= PLAUSIBLE_MATCH ? C.amber : C.textMuted;
  return (
    <span
      role="img"
      aria-label={t('secretary.match.score', { score: formatDigits(score) })}
      style={{ display: 'grid', justifyItems: 'end', gap: 4, flexShrink: 0 }}
      data-testid="secretary-match-score"
    >
      <span aria-hidden="true" style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatDigits(score)}</span>
      <span aria-hidden="true" style={{ display: 'block', width: 64, height: 5, borderRadius: 99, background: C.border, overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(100, score))}%`, marginInlineStart: 'auto', borderRadius: 99, background: tone }} />
      </span>
    </span>
  );
}

/** Why a candidate matched — the BFF's reason codes, in the reader's language. */
function MatchReasons({ reasons, t }) {
  const known = (reasons || []).filter((r) => MATCH_REASONS.includes(r?.kind));
  if (!known.length) return null;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
      {known.map((r, i) => (
        <Badge key={`${r.kind}-${i}`} variant={r.kind === 'appointment_soon' ? 'default' : 'outline'} data-testid={`secretary-reason-${r.kind}`}>
          <span dir="auto">{t(`secretary.match.reason.${r.kind}`, { heard: r.heard || '', stored: r.stored || '' })}</span>
        </Badge>
      ))}
    </span>
  );
}

/** One key of a list, bound while its row is on screen — labelled, so «?» lists it. */
function PickKey({ combo, label, group, onPress }) {
  useShortcut(combo, onPress, { label, group });
  return null;
}

/**
 * A list the desk answers with ONE key: 1–5 pick a row, 0 says «بیمار جدید است» —
 * offered only when the command was a booking and a name was heard. «کدام‌یک؟»,
 * «بیمار دیگری است؟» and the closest names under «پیدا نکردم» are all this list.
 */
function PatientChoice({ title, hint, patients, newPatientName = null, onPick, onNew, onBack = null, compact = false, tr }) {
  const { t, formatDigits } = tr;
  const rows = (patients || []).slice(0, PICK_KEYS.length);
  const group = t('secretary.shortcuts.open');
  const offerNew = Boolean(newPatientName && onNew);
  return (
    <div data-testid="secretary-patient-choice">
      {compact
        ? <div style={{ ...S.label, marginBottom: 6 }}>{title}</div>
        : <h3 style={S.title}>{title}</h3>}
      {hint ? <p style={S.hint}>{hint}</p> : null}
      {rows.map((p, i) => (
        <button
          key={p.id}
          type="button"
          autoFocus={i === 0 && !compact}
          style={{ ...pick, borderColor: i === 0 && !compact ? C.navy : C.border }}
          onClick={() => onPick(p.id)}
          aria-keyshortcuts={PICK_KEYS[i]}
          data-testid={`secretary-patient-${p.id}`}
        >
          <PickKey combo={PICK_KEYS[i]} label={t('secretary.shortcuts.pickPatient', { n: formatDigits(i + 1) })} group={group} onPress={() => onPick(p.id)} />
          <KeyCap combo={PICK_KEYS[i]} style={S.kbdOnLight} />
          <span aria-hidden="true" style={{ ...S.disc, width: 40, height: 40, background: soft(C.navy, 0.1), color: C.navy }}><User size={20} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <PatientLine patient={p} t={t} formatDigits={formatDigits} />
            <MatchReasons reasons={p.reasons} t={t} />
          </span>
          <MatchScore score={p.score} t={t} formatDigits={formatDigits} />
        </button>
      ))}
      {offerNew ? (
        <button
          type="button"
          style={{ ...pick, borderStyle: 'dashed' }}
          onClick={onNew}
          aria-keyshortcuts={NEW_PATIENT_KEY}
          data-testid="secretary-new-patient"
        >
          <PickKey combo={NEW_PATIENT_KEY} label={t('secretary.shortcuts.newPatient')} group={group} onPress={onNew} />
          <KeyCap combo={NEW_PATIENT_KEY} style={S.kbdOnLight} />
          <span aria-hidden="true" style={{ ...S.disc, width: 40, height: 40, background: soft(C.teal, 0.14), color: C.teal }}><UserPlus size={20} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{t('secretary.choosePatient.newPatient')}</span>
            <span dir="auto" style={{ display: 'block', fontSize: 13, color: C.textSecondary }}>
              {t('secretary.choosePatient.newPatientHint', { name: newPatientName })}
            </span>
          </span>
        </button>
      ) : null}
      {onBack ? (
        <div style={{ ...S.row, justifyContent: 'flex-start', marginTop: 6 }}>
          <Button type="button" variant="outline" onClick={onBack} data-testid="secretary-choice-back">{t('secretary.choosePatient.back')}</Button>
        </div>
      ) : null}
    </div>
  );
}

/** «شنیدم» — what the server transcribed, with the two ways to fix it. */
function HeardCard({ text, onAgain, onContinue, onRunEdited, onEdit, t }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(text);
  React.useEffect(() => { setDraft(text); setEditing(false); }, [text]);
  return (
    <section style={{ ...S.card, padding: 16, marginBottom: 14 }} data-testid="secretary-heard">
      <div style={{ ...S.label, marginBottom: 6 }}>{t('secretary.heard.label')}</div>
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); onRunEdited(draft); }}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={t('secretary.heard.editLabel')}
            data-testid="secretary-heard-input"
            autoComplete="off"
            dir="auto"
            style={{
              width: '100%', boxSizing: 'border-box', height: 44, padding: '0 12px', borderRadius: 10,
              border: `1.5px solid ${C.border}`, background: C.card, color: C.textPrimary,
              fontFamily: 'inherit', fontSize: 16,
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            <Button type="submit" size="sm" disabled={!draft.trim()} data-testid="secretary-heard-run">{t('secretary.heard.runEdited')}</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>{t('secretary.heard.cancelEdit')}</Button>
          </div>
        </form>
      ) : (
        <>
          {text ? <div dir="auto" style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary }} data-testid="secretary-heard-text">{text}</div> : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            <Button type="button" size="sm" variant="outline" onClick={onAgain} data-testid="secretary-again">
              <Mic size={16} aria-hidden="true" /> {t('secretary.heard.again')}
            </Button>
            {text && onContinue ? (
              <Button type="button" size="sm" variant="outline" onClick={onContinue} aria-keyshortcuts="Shift+F1" data-testid="secretary-continue">
                <MessageSquarePlus size={16} aria-hidden="true" /> {t('secretary.heard.continue')} <KeyCap combo="shift+f1" style={S.kbdOnLight} />
              </Button>
            ) : null}
            {text ? (
              <Button type="button" size="sm" variant="outline" onClick={() => { onEdit?.(); setEditing(true); }} data-testid="secretary-heard-edit">
                {t('secretary.heard.edit')}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

/** «برداشت من» — the structured reading of the sentence. An assumption says it is one. */
function UnderstoodCard({ result, onOtherPatient, t, formatDate, formatTime, formatDigits }) {
  const patient = resultPatient(result);
  const facts = [];
  if (result.kind && t(`secretary.action.${result.kind}`)) {
    facts.push({ key: 'action', label: t('secretary.understood.action'), value: t(`secretary.action.${result.kind}`) });
  }
  if (patient) {
    // How the name was matched travels with the fact, like the assumed doctor does: a
    // score and its strongest reason, or «بیمار جدید» for nobody on file.
    const reason = (patient.reasons || []).find((r) => MATCH_REASONS.includes(r?.kind));
    facts.push({
      key: 'patient',
      label: t('secretary.understood.patient'),
      value: patient.fileNumber
        ? t('secretary.understood.patientFile', { name: patient.name, file: formatDigits(patient.fileNumber) })
        : patient.name,
      extra: (
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 4 }}>
          {patient.isNew ? (
            <Badge variant="success" data-testid="secretary-understood-new">{t('secretary.match.newPatient')}</Badge>
          ) : patient.score != null ? (
            <Badge variant={patient.score >= STRONG_MATCH ? 'success' : 'warning'} data-testid="secretary-understood-match">
              <span dir="auto">
                {t('secretary.match.score', { score: formatDigits(patient.score) })}
                {reason ? ` · ${t(`secretary.match.reason.${reason.kind}`, { heard: reason.heard || '', stored: reason.stored || '' })}` : ''}
              </span>
            </Badge>
          ) : null}
          {onOtherPatient ? (
            <button
              type="button"
              onClick={onOtherPatient}
              style={{ border: 0, padding: 0, background: 'none', color: C.navy, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
              data-testid="secretary-other-patient-link"
            >
              {t('secretary.match.otherPatient')}
            </button>
          ) : null}
        </span>
      ),
    });
  }
  if (result.booking) {
    facts.push({
      key: 'window',
      label: t('secretary.understood.window'),
      value: t('secretary.understood.windowValue', {
        from: formatDate(result.booking.fromMs, 'dayMonth'), to: formatDate(result.booking.toMs, 'dayMonth'),
      }),
    });
    const assumed = result.booking.doctorAssumed;
    facts.push({
      key: 'doctor',
      label: t('secretary.understood.doctor'),
      value: result.booking.doctor.name,
      note: assumed === 'last_doctor' ? t('secretary.understood.assumedLastDoctor')
        : assumed === 'only_doctor' ? t('secretary.understood.assumedOnlyDoctor') : null,
    });
  }
  if (result.booking?.requestedAt) {
    facts.push({ key: 'time', label: t('secretary.understood.time'), value: formatTime(result.booking.requestedAt) });
  }
  if (result.followUp?.dueAt) {
    facts.push({ key: 'due', label: t('secretary.understood.due'), value: formatDate(result.followUp.dueAt, 'weekdayDayMonth') });
  }
  if (!facts.length) return null;
  return (
    <section style={{ ...S.card, padding: 16 }} data-testid="secretary-understood">
      <div style={{ ...S.label, marginBottom: 2 }}>{t('secretary.understood.label')}</div>
      {facts.map((f) => (
        <div key={f.key} style={{ padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12.5, color: C.textSecondary }}>{f.label}</div>
          <div dir="auto" style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{f.value}</div>
          {f.extra || null}
          {f.note ? <Badge variant="warning" style={{ marginTop: 4 }} data-testid={`secretary-assumed-${f.key}`}>{f.note}</Badge> : null}
        </div>
      ))}
    </section>
  );
}

function ProposalHead({ title, t }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <h3 style={S.title}>{title}</h3>
      <Badge data-testid="secretary-ai-badge">{t('secretary.aiBadge')}</Badge>
    </div>
  );
}

/**
 * The hour the desk NAMED («ساعت ۱ بعد از ظهر») and how it stood: free (it is the
 * proposal), taken (the nearest free times follow), or not judged (an older service).
 */
function RequestedHour({ booking, t, formatDate, formatTime }) {
  if (!booking?.requestedAt) return null;
  const when = t('secretary.booking.when', {
    day: formatDate(booking.requestedAt, 'weekdayDayMonth'), time: formatTime(booking.requestedAt),
  });
  const free = booking.requestedFree;
  const text = free === true ? t('secretary.booking.requestedFree', { when })
    : free === false ? t('secretary.booking.requestedTaken', { when })
      : t('secretary.booking.requestedUnchecked', { when });
  return (
    <Alert
      variant={free === true ? 'success' : 'warning'}
      style={{ marginTop: 12 }}
      data-testid="secretary-requested-hour"
      data-free={free === null || free === undefined ? 'unknown' : String(free)}
    >
      {text}
    </Alert>
  );
}

/**
 * A booking for a patient the clinic has no record of. Says so, says why it might be
 * wrong when a close record exists, and keeps the closest names one press away.
 */
function NewPatientNotice({
  name, similar, details, onClosest, onContinue, onBookExisting, skipped = null, asking = false, t, formatDigits,
}) {
  const missing = details?.missing || [];
  // «رد کردن» on the sheet's question: the form asks for it, and the row says so.
  const later = (key) => (skipped?.has(key) ? t('secretary.newPatient.skipped') : null);
  // The records already on the mobile that was said — the same list «ثبت بیمار جدید»
  // shows before it creates anyone. A mobile is a household: the person at the desk may
  // be one of these, and the desk says which before a second file is opened.
  const onNumber = usePhoneCandidates(details?.phone || '', { delay: 0 });
  const linkAndBook = async (c) => {
    const data = await bffMutation(LINK_EXISTING_PATIENT, { patientId: c.patientId });
    const linked = data?.linkExistingPatient;
    if (!linked?.id) throw new Error('linkExistingPatient returned no patient');
    onBookExisting(linked);
  };
  // What «ثبت نوبت» needs to register them, as it was heard — and what was not.
  const rows = [
    { key: 'name', value: name, ok: true },
    {
      key: 'phone',
      value: details?.phone ? formatDigits(details.phone)
        : details?.phoneHeard ? t('secretary.newPatient.phoneMisheard', { heard: formatDigits(details.phoneHeard) }) : later('phone'),
      ok: !missing.includes('phone'),
      ltr: !!details?.phone,
    },
    {
      key: 'insurance',
      value: details?.insurerName
        || (details?.insurance ? t('secretary.newPatient.insuranceUnmatched', { said: details.insurance }) : later('insurance')),
      ok: !missing.includes('insurance'),
    },
  ];
  if (details?.nationalId) rows.push({ key: 'nationalId', value: formatDigits(details.nationalId), ok: true, ltr: true });
  return (
    <div
      style={{
        display: 'flex', gap: 12, alignItems: 'flex-start', margin: '12px 0 4px', padding: '12px 14px',
        borderRadius: 10, background: soft(C.teal, 0.07), border: `1px solid ${soft(C.teal, 0.28)}`,
      }}
      data-testid="secretary-new-patient-notice"
    >
      <span aria-hidden="true" style={{ ...S.disc, width: 40, height: 40, background: soft(C.teal, 0.16), color: C.teal }}><UserPlus size={20} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div dir="auto" style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary }}>
          {similar ? t('secretary.newPatient.titleSaid', { name }) : t('secretary.newPatient.title', { name })}
        </div>
        <div style={{ fontSize: 13, color: similar ? C.amber : C.textSecondary, marginTop: 2 }}>
          {similar ? t('secretary.newPatient.similar') : t('secretary.newPatient.hint')}
        </div>
        {details ? (
          <div style={{ display: 'grid', gap: 4, margin: '10px 0 4px' }} data-testid="secretary-new-patient-checklist">
            {rows.map((r) => (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }} data-testid={`secretary-new-${r.key}`} data-ok={r.ok ? 'true' : 'false'}>
                <span aria-hidden="true" style={{ display: 'inline-grid', placeItems: 'center', width: 18, height: 18, borderRadius: 999, background: r.ok ? soft(C.teal, 0.18) : soft(C.amber, 0.2), color: r.ok ? C.teal : C.amber }}>
                  {r.ok ? <Check size={12} /> : <X size={12} />}
                </span>
                <span style={{ minWidth: 64, color: C.textSecondary }}>{t(`secretary.newPatient.field.${r.key}`)}</span>
                <span dir={r.ltr ? 'ltr' : 'auto'} style={{ fontWeight: 700, color: r.value ? C.textPrimary : C.textMuted, unicodeBidi: 'isolate' }}>
                  {r.value || t('secretary.newPatient.notSaid')}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {onNumber.candidates.length && onBookExisting ? (
          <div style={{ margin: '10px 0 4px' }} data-testid="secretary-same-phone">
            <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>{t('secretary.newPatient.samePhoneTitle')}</div>
            <div style={{ fontSize: 12.5, color: C.textSecondary, margin: '2px 0 8px' }}>{t('secretary.newPatient.samePhoneHint')}</div>
            <PhoneCandidateList
              candidates={onNumber.candidates}
              onChooseLinked={(c) => onBookExisting(c.patient || { id: c.patientId, name: c.displayName, phone: details.phone })}
              chooseLabel={t('secretary.newPatient.bookForThis')}
              onLink={linkAndBook}
              linkLabel={t('patients.phoneCandidates.addAndChoose')}
              data-testid="secretary-same-phone-list"
            />
          </div>
        ) : null}
        {missing.length && !asking ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{ fontSize: 12.5, color: C.textSecondary }}>
              {t('secretary.newPatient.missingHint', { list: missing.map((m) => t(`secretary.newPatient.missing.${m}`)).join(t('secretary.newPatient.and')) })}
            </span>
            {onContinue ? (
              <Button type="button" size="sm" onClick={onContinue} aria-keyshortcuts="Shift+F1" data-testid="secretary-new-patient-continue">
                <MessageSquarePlus size={16} aria-hidden="true" /> {t('secretary.heard.continue')} <KeyCap combo="shift+f1" />
              </Button>
            ) : null}
          </div>
        ) : details && !missing.length ? (
          <div style={{ fontSize: 12.5, color: C.teal, marginTop: 6 }} data-testid="secretary-new-patient-complete">{t('secretary.newPatient.complete')}</div>
        ) : null}
        {onClosest ? (
          <button
            type="button"
            onClick={onClosest}
            style={{ marginTop: 6, border: 0, padding: 0, background: 'none', color: C.navy, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
            data-testid="secretary-new-patient-closest"
          >
            {t('secretary.newPatient.closest')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

const Q = {
  card: { ...S.card, padding: 16, border: `1.5px solid ${soft(C.navy, 0.3)}` },
  link: {
    display: 'inline-flex', alignItems: 'center', gap: 6, border: 0, padding: 0, background: 'none', color: C.navy,
    fontFamily: 'inherit', fontSize: 13, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer',
  },
};

/**
 * What the sentence left out (PM-0080) — ONE question, above what was prepared.
 * A quick answer is a tap or a key (1–5) and costs nothing. A blocking question is
 * already listening when it appears, and the recording ends on its own after a
 * moment of silence; a spoken mobile is read back before it is used; «رد کردن» (0)
 * leaves a mobile or an insurer to the form. «برای چه زمانی؟» is an OFFER: the first
 * free times below stand until it is answered.
 */
function QuestionCard({ session, tr }) {
  const { t, formatDate, formatDigits } = tr;
  const { question: q, answer: a, result } = session;
  const [changing, setChanging] = React.useState(false);
  React.useEffect(() => { setChanging(false); }, [result]);
  if (!q) return null;

  const group = t('secretary.shortcuts.open');
  const stage = a?.stage || 'idle';
  const busy = stage === 'refining' || stage === 'hearing';
  const listening = stage === 'listening';
  const live = listening && session.micStatus === 'listening';
  const title = t(`secretary.question.title.${q.kind}`);
  const chosen = q.options.find((o) => o.selected) || null;

  // «برای چه زمانی؟» answered with a chip: one line, and «تغییر» to open it again.
  if (q.kind === 'when' && chosen && chosen.value !== 'first' && !changing && stage === 'idle' && !a?.note) {
    const b = result?.booking;
    const span = !b ? '' : chosen.value === 'next_week'
      ? t('secretary.understood.windowValue', { from: formatDate(b.fromMs, 'dayMonth'), to: formatDate(b.toMs, 'dayMonth') })
      : formatDate(b.fromMs, 'weekdayDayMonth');
    return (
      <section
        style={{ ...Q.card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        data-testid="secretary-question"
        data-kind="when"
        data-answered="true"
      >
        <span style={{ fontSize: 14, color: C.textSecondary }}>
          {t('secretary.question.answeredWhen')}{' '}
          <b style={{ color: C.textPrimary }}>{chosen.label}</b>
          {span ? <span>{` (${span})`}</span> : null}
        </span>
        <button type="button" style={Q.link} onClick={() => setChanging(true)} data-testid="secretary-question-change">
          {t('secretary.question.change')}
        </button>
      </section>
    );
  }

  const note = q.kind === 'doctor' || q.kind === 'when' ? t(`secretary.question.note.${q.kind}`) : null;
  const step = q.step
    ? t('secretary.question.step', { index: formatDigits(q.step.index), total: formatDigits(q.step.total) })
    : t('secretary.question.optional');
  return (
    <section style={Q.card} data-testid="secretary-question" data-kind={q.kind} data-stage={stage} aria-label={title}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 style={{ ...S.title, fontSize: 16 }}>{title}</h3>
        <Badge variant="outline" data-testid="secretary-question-step">{step}</Badge>
      </div>
      {note ? <p style={{ ...S.hint, margin: '4px 0 0' }}>{note}</p> : null}

      {stage === 'readback' ? (
        // Speech gets digits wrong: the number is read back, and only a «بله» uses it.
        <div style={{ marginTop: 10, textAlign: 'center' }} data-testid="secretary-readback">
          <div dir="ltr" style={{ ...S.ltr, fontSize: 26, fontWeight: 800, letterSpacing: 1, color: C.textPrimary }} data-testid="secretary-readback-digits">
            {digitGroups(a.digits).map((g) => formatDigits(g)).join(' ')}
          </div>
          <div style={{ fontSize: 13.5, color: C.textSecondary, marginTop: 2 }}>{t('secretary.question.readBack')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 10 }}>
            <Button type="button" variant="outline" onClick={() => session.answerByVoice()} data-testid="secretary-readback-again">
              <Mic size={16} aria-hidden="true" /> {t('secretary.question.sayAgain')}
            </Button>
            <Button type="button" autoFocus onClick={session.confirmPhone} aria-keyshortcuts="Enter" data-testid="secretary-readback-confirm">
              {t('secretary.question.confirm')} <KeyCap combo="enter" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          {q.options.length || !listening ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }} data-testid="secretary-answers">
              {q.options.map((o, i) => {
                const key = i < PICK_KEYS.length ? PICK_KEYS[i] : null;
                const doctor = q.kind === 'doctor' ? (result?.doctors || []).find((d) => d.doctorId === o.value) : null;
                return (
                  <FilterChip
                    key={o.value}
                    active={o.selected}
                    disabled={busy}
                    onClick={() => session.answerWith(o.value)}
                    aria-keyshortcuts={key || undefined}
                    data-testid={`secretary-answer-${o.value}`}
                  >
                    {key && !busy ? (
                      <PickKey combo={key} label={t('secretary.shortcuts.answer', { n: formatDigits(i + 1) })} group={group} onPress={() => session.answerWith(o.value)} />
                    ) : null}
                    <span dir="auto">{o.label}</span>
                    {doctor?.specialty ? <span dir="auto" style={{ fontWeight: 400, opacity: 0.75 }}>{`· ${doctor.specialty}`}</span> : null}
                    {key ? <KeyCap combo={key} style={S.kbdOnLight} /> : null}
                  </FilterChip>
                );
              })}
              {!listening ? (
                <FilterChip disabled={busy} onClick={() => session.answerByVoice()} data-testid="secretary-answer-speak">
                  <Mic size={14} aria-hidden="true" /> {t('secretary.question.speak')}
                </FilterChip>
              ) : null}
            </div>
          ) : null}
          {listening ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 10 }} data-testid="secretary-answer-listening" data-manual={a.manual ? 'true' : 'false'}>
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 999, flexShrink: 0, background: live ? C.red : C.border }} />
              <span aria-live="polite" style={{ flex: '1 1 200px', minWidth: 0, fontSize: 13.5, color: C.textSecondary }}>
                {!live ? t('secretary.question.preparing') : a.manual ? t('secretary.question.listeningManual') : t('secretary.question.listening')}
              </span>
              {live && a.manual ? (
                <Button type="button" size="sm" onClick={session.finishAnswer} data-testid="secretary-answer-stop">{t('secretary.question.stopListening')}</Button>
              ) : null}
              <Button type="button" size="sm" variant="ghost" onClick={session.cancelAnswer} data-testid="secretary-answer-cancel">
                {t('secretary.question.cancelListening')}
              </Button>
            </div>
          ) : null}
          {busy ? (
            <div aria-live="polite" style={{ marginTop: 10, fontSize: 13.5, color: C.textSecondary }} data-testid="secretary-answer-busy">
              {stage === 'hearing' ? t('secretary.question.hearing') : t('secretary.question.refining')}
            </div>
          ) : null}
        </>
      )}

      {a?.note ? (
        <div role="status" style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: C.amber }} data-testid="secretary-answer-note" data-note={a.note}>
          <span dir="auto">{t(`secretary.question.problem.${a.note}`, { heard: a.heard ? formatDigits(a.heard) : '' })}</span>
        </div>
      ) : null}
      {SKIPPABLE_QUESTIONS.includes(q.kind) ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" style={Q.link} onClick={session.skipQuestion} disabled={busy} aria-keyshortcuts={SKIP_KEY} data-testid="secretary-question-skip">
            {!busy ? <PickKey combo={SKIP_KEY} label={t('secretary.shortcuts.skip')} group={group} onPress={session.skipQuestion} /> : null}
            {t('secretary.question.skip')} <KeyCap combo={SKIP_KEY} style={S.kbdOnLight} />
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** What was prepared — one branch per outcome. `choosing`: «بیمار دیگری است؟» is open. */
function Prepared({ session, choosing, onChoosing, onBook, onDone, onOpenPatient, onClose, tr }) {
  const { t, formatDate, formatTime, formatDigits, formatNumber, formatMoney } = tr;
  const { result } = session;
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState('');
  React.useEffect(() => { setFailed(''); setBusy(false); }, [result]);

  const commit = async (document, variables, doneMessage, failMessage, after) => {
    setBusy(true);
    setFailed('');
    try {
      await bffMutation(document, variables);
      after?.();
      onDone(doneMessage);
    } catch {
      // The form of this write is this card: it reports here, keeps what it shows,
      // and the button comes back (DESIGN_CONCEPT §2.4).
      setFailed(failMessage);
      setBusy(false);
    }
  };

  const primary = (() => {
    if (busy || choosing) return null;
    // The form opens on the first proposed time; it lists the others itself.
    if (result.outcome === 'PROPOSAL' && result.kind === 'book_appointment') return () => onBook(bookingHandoff(result.booking, 0));
    if (result.outcome === 'PROPOSAL' && result.kind === 'mark_arrived') {
      return () => commit(SECRETARY_MARK_ARRIVED, { appointmentId: result.arrival.appointmentId },
        t('secretary.arrival.done'), t('secretary.arrival.failed'), reportAppointmentsChanged);
    }
    if (result.outcome === 'PROPOSAL' && result.kind === 'follow_up') {
      const f = result.followUp;
      // No day yet: «کی پیگیری شود؟» is open above, and nothing is saved without one.
      if (!f.dueAt) return null;
      return () => commit(SECRETARY_CREATE_FOLLOW_UP,
        { input: { patientId: f.patient.id, dueAt: f.dueAt, kind: f.kind, note: f.note || null } },
        t('secretary.followUp.done'), t('secretary.followUp.failed'));
    }
    if (result.outcome === 'NAVIGATE' && result.patient) return () => onOpenPatient(result.patient.id);
    return null;
  })();

  useShortcut('ctrl+enter', () => primary?.(), {
    label: t('common.actions.confirm'), group: t('secretary.shortcuts.open'), enabled: Boolean(primary),
  });

  const submitKbd = <KeyCap combo="ctrl+enter" />;
  const error = failed ? <Alert variant="error" style={{ marginTop: 12 }} data-testid="secretary-commit-error">{failed}</Alert> : null;

  const booking = result.kind === 'book_appointment';
  const heard = result.heardName || null;

  if (choosing) {
    // «بیمار دیگری است؟» — the lookup's other candidates; or, under a NEW patient,
    // the closest names that were checked. Picking one re-runs the sentence with it.
    const isNew = !!result.booking?.patient?.isNew;
    return (
      <div data-testid="secretary-other-patient">
        <PatientChoice
          title={isNew ? t('secretary.choosePatient.closestTitle') : t('secretary.choosePatient.otherTitle')}
          hint={t('secretary.choosePatient.otherHint')}
          patients={isNew ? (result.patients || []) : otherCandidates(result)}
          newPatientName={booking && !isNew ? heard : null}
          onPick={session.choosePatient}
          onNew={session.chooseNewPatient}
          onBack={() => onChoosing(false)}
          tr={tr}
        />
      </div>
    );
  }

  switch (result.outcome) {
    case 'CHOOSE_PATIENT':
      return (
        <div data-testid="secretary-choose-patient">
          <PatientChoice
            title={t('secretary.choosePatient.title')}
            hint={heard ? t('secretary.choosePatient.hintHeard', { name: heard }) : t('secretary.choosePatient.hint')}
            patients={result.patients || []}
            newPatientName={booking ? heard : null}
            onPick={session.choosePatient}
            onNew={session.chooseNewPatient}
            tr={tr}
          />
          {booking && heard ? null : (
            <Button type="button" variant="outline" onClick={session.again}>{t('secretary.choosePatient.none')}</Button>
          )}
        </div>
      );

    case 'CHOOSE_DOCTOR':
      if (session.question?.kind === 'doctor') {
        // «با کدام پزشک؟» is the question above: free, with a key per doctor.
        return (
          <div data-testid="secretary-choose-doctor" data-asked="true">
            <h3 style={S.title}>{t('secretary.chooseDoctor.waitTitle')}</h3>
            <p style={{ ...S.hint, marginBottom: 0 }}>{t('secretary.chooseDoctor.waitHint')}</p>
          </div>
        );
      }
      return (
        <div data-testid="secretary-choose-doctor">
          <h3 style={S.title}>{t('secretary.chooseDoctor.title')}</h3>
          <p style={S.hint}>{t('secretary.chooseDoctor.hint')}</p>
          {(result.doctors || []).map((d, i) => (
            <button key={d.doctorId} type="button" autoFocus={i === 0} style={pick} onClick={() => session.chooseDoctor(d.doctorId)} data-testid={`secretary-doctor-${d.doctorId}`}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span dir="auto" style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{d.name}</span>
                {d.specialty ? <span dir="auto" style={{ display: 'block', fontSize: 13, color: C.textSecondary }}>{d.specialty}</span> : null}
              </span>
            </button>
          ))}
        </div>
      );

    case 'PROPOSAL':
      if (result.kind === 'book_appointment') {
        // This window opens for a booking only while something must be answered
        // first; the time is chosen in «ثبت نوبت», which opens on the first one.
        const first = result.booking.slots?.[0] || null;
        const isNew = !!result.booking.patient?.isNew;
        return (
          <div data-testid="secretary-booking">
            <ProposalHead title={t('secretary.window.bookingTitle', { name: result.booking.patient.name })} t={t} />
            {first ? (
              <div style={{ marginTop: 6, fontSize: 14.5, fontWeight: 700, color: C.textPrimary }} data-testid="secretary-booking-when">
                {t('secretary.window.bookingWhen', {
                  day: formatDate(first.dateTime, 'weekdayDayMonth'),
                  time: formatTime(first.dateTime),
                  doctor: result.booking.doctor.name,
                })}
              </div>
            ) : null}
            {isNew ? (
              <NewPatientNotice
                name={result.booking.patient.name}
                similar={result.reason === 'similar_patient'}
                details={result.booking.newPatient}
                onClosest={result.patients?.length ? () => onChoosing(true) : null}
                onContinue={session.continueSpeaking}
                skipped={session.skipped}
                asking={Boolean(result.draft)}
                // A record already on the number: book the proposed hour for IT, in the form.
                onBookExisting={(p) => onBook({
                  ...bookingHandoff(result.booking, 0),
                  patient: { id: p.id, name: p.name || '', phone: p.phone || '' },
                  newPatient: null,
                })}
                t={t}
                formatDigits={formatDigits}
              />
            ) : null}
            <RequestedHour booking={result.booking} t={t} formatDate={formatDate} formatTime={formatTime} />
            <div style={{ ...S.row, justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: C.textSecondary }}>{t('secretary.notSaved')}</span>
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
                <Button type="button" variant="outline" onClick={onClose} data-testid="secretary-dismiss">{t('secretary.cancel')}</Button>
                <Button type="button" autoFocus onClick={primary} aria-keyshortcuts="Control+Enter" data-testid="secretary-booking-continue">
                  {isNew ? t('secretary.booking.continueNew') : t('secretary.booking.continue')} {submitKbd}
                </Button>
              </span>
            </div>
          </div>
        );
      }
      if (result.kind === 'mark_arrived') {
        const a = result.arrival;
        return (
          <div data-testid="secretary-arrival">
            <ProposalHead title={t('secretary.arrival.title')} t={t} />
            <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg }}>
              <div dir="auto" style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>{a.patient.name}</div>
              {a.dateTime ? (
                <div style={{ fontSize: 13.5, color: C.textSecondary, marginBottom: 10 }}>
                  {a.doctorName
                    ? t('secretary.arrival.today', { time: formatTime(a.dateTime), doctor: a.doctorName })
                    : t('secretary.arrival.todayNoDoctor', { time: formatTime(a.dateTime) })}
                </div>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13, color: C.textSecondary }}>
                {t('secretary.arrival.status')}
                <StatusBadge status="scheduled" />
                {/* Drawn as authored for RTL («becomes» points to the inline-end); the shared flip mirrors it for LTR. */}
                <ChevronLeft size={16} aria-hidden="true" style={{ transform: 'scaleX(var(--dir-flip, 1))' }} />
                <StatusBadge status="arrived" />
              </div>
            </div>
            {error}
            <div style={{ ...S.row, justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: C.textSecondary }}>{t('secretary.notSaved')}</span>
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
                <Button type="button" variant="outline" onClick={onClose} disabled={busy}>{t('secretary.cancel')}</Button>
                <Button type="button" autoFocus onClick={primary} disabled={busy} aria-keyshortcuts="Control+Enter" data-testid="secretary-arrival-commit">
                  {t('secretary.arrival.commit')} {submitKbd}
                </Button>
              </span>
            </div>
          </div>
        );
      }
      if (result.kind === 'follow_up') {
        const f = result.followUp;
        const kind = FOLLOW_UP_KINDS.includes(f.kind) ? f.kind : 'other';
        return (
          <div data-testid="secretary-followup">
            <ProposalHead title={t('secretary.followUp.title')} t={t} />
            <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, fontSize: 14 }}>
              <div dir="auto" style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>{f.patient.name}</div>
              {f.dueAt ? (
                <div style={{ color: C.textSecondary }}>{t('secretary.understood.due')}: {formatDate(f.dueAt, 'weekdayDayMonth')}</div>
              ) : (
                <div style={{ color: C.amber, fontWeight: 700 }} data-testid="secretary-followup-no-due">{t('secretary.followUp.noDue')}</div>
              )}
              <div style={{ color: C.textSecondary }}>{t('secretary.followUp.kind')}: {t(`patients.followUp.kind.${kind}`)}</div>
              {f.note ? <div dir="auto" style={{ color: C.textSecondary }}>{t('secretary.followUp.note')}: {f.note}</div> : null}
            </div>
            {error}
            <div style={{ ...S.row, justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: C.textSecondary }}>{t('secretary.notSaved')}</span>
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
                <Button type="button" variant="outline" onClick={onClose} disabled={busy}>{t('secretary.cancel')}</Button>
                <Button type="button" autoFocus={Boolean(f.dueAt)} onClick={primary} disabled={busy || !f.dueAt} aria-keyshortcuts="Control+Enter" data-testid="secretary-followup-commit">
                  {t('secretary.followUp.commit')} {submitKbd}
                </Button>
              </span>
            </div>
          </div>
        );
      }
      break;

    case 'NO_SLOT':
      return (
        <div data-testid="secretary-no-slot">
          <Verdict
            icon={<Clock size={22} />}
            title={result.booking?.requestedAt
              ? t('secretary.booking.noSlotNear', { day: formatDate(result.booking.requestedAt, 'weekdayDayMonth'), time: formatTime(result.booking.requestedAt) })
              : t('secretary.booking.noSlotTitle')}
          >
            {t('secretary.booking.noSlotHint')}
          </Verdict>
          <div style={S.row}>
            <Button type="button" variant="outline" autoFocus onClick={() => onBook(bookingHandoff(result.booking, -1))} data-testid="secretary-open-form">
              {t('secretary.booking.openForm')}
            </Button>
          </div>
        </div>
      );

    case 'NOTHING_TO_DO':
      return (
        <div data-testid="secretary-nothing">
          <Verdict icon={<Check size={22} />} title={t('secretary.arrival.nothingTitle')}>{t('secretary.arrival.nothingHint')}</Verdict>
          {result.arrival ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
              <span dir="auto" style={{ fontWeight: 700 }}>{result.arrival.patient.name}</span>
              <StatusBadge status={result.arrival.status} />
            </div>
          ) : null}
        </div>
      );

    case 'NAVIGATE':
      return (
        <div data-testid="secretary-open-patient">
          <h3 style={S.title}>{t('secretary.openPatient.title')}</h3>
          <div style={{ ...pick, cursor: 'default', marginTop: 14 }}>
            <span aria-hidden="true" style={{ ...S.disc, width: 40, height: 40, background: soft(C.navy, 0.1), color: C.navy }}><User size={20} /></span>
            <PatientLine patient={result.patient} t={t} formatDigits={formatDigits} />
          </div>
          <div style={S.row}>
            <Button type="button" autoFocus onClick={primary} aria-keyshortcuts="Control+Enter" data-testid="secretary-open-patient-go">
              {t('secretary.openPatient.open')} {submitKbd}
            </Button>
          </div>
        </div>
      );

    case 'ANSWER':
      if (result.kind === 'patient_balance' && result.balance) {
        const b = result.balance;
        return (
          <div data-testid="secretary-balance">
            <h3 style={S.title}>{t('secretary.balance.title', { name: b.patient.name })}</h3>
            <p style={{ ...S.hint, marginBottom: 0 }}>{t('secretary.balance.source')}</p>
            {b.outstanding > 0 ? (
              <>
                <div dir="ltr" style={{ ...S.ltr, margin: '12px 0 4px', fontSize: 34, fontWeight: 800, color: C.textPrimary, whiteSpace: 'nowrap', textAlign: 'end' }} data-testid="secretary-balance-amount">
                  {formatMoney(b.outstanding, { currency: b.currency, withUnit: true })}
                </div>
                <div style={{ fontSize: 14, color: C.textSecondary }}>{t('secretary.balance.openInvoices', { count: formatNumber(b.openInvoices) })}</div>
              </>
            ) : (
              <div style={{ margin: '12px 0', fontSize: 15, fontWeight: 700 }} data-testid="secretary-balance-none">{t('secretary.balance.none')}</div>
            )}
            <div style={S.row}>
              <Button type="button" variant="outline" onClick={() => onOpenPatient(b.patient.id)}>{t('secretary.balance.openRecord')}</Button>
            </div>
          </div>
        );
      }
      if (result.kind === 'day_summary' && result.daySummary) {
        const d = result.daySummary;
        const cells = ['total', 'scheduled', 'arrived', 'inVisit', 'completed', 'cancelled', 'noShow'];
        return (
          <div data-testid="secretary-day-summary">
            <h3 style={S.title}>{t('secretary.daySummary.title')}</h3>
            <p style={S.hint}>{t('secretary.daySummary.source')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
              {cells.map((k) => (
                <div key={k} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg }}>
                  <div style={{ fontSize: 12.5, color: C.textSecondary }}>{t(`secretary.daySummary.${k}`)}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }} data-testid={`secretary-day-${k}`}>{formatNumber(d[k])}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      break;

    case 'UNAVAILABLE':
      return <Verdict testid="secretary-unavailable" icon={<CircleHelp size={22} />} title={t('secretary.unavailable.title')}>{t('secretary.unavailable.hint')}</Verdict>;

    case 'FORBIDDEN': {
      const keys = result.missingPermissions || [];
      return (
        <Verdict testid="secretary-forbidden" icon={<Lock size={22} />} title={t('secretary.forbidden.title')}>
          {keys.length ? <div>{t('secretary.forbidden.needs', { list: keys.map((k) => t(`people.permissions.${k}.label`)).join(' · ') })}</div> : null}
          <div>{t('secretary.forbidden.hint')}</div>
        </Verdict>
      );
    }

    case 'OUT_OF_SCOPE': {
      const topic = OUT_OF_SCOPE_TOPICS.includes(result.topic) ? result.topic : 'other';
      return (
        <Verdict testid="secretary-out-of-scope" tone="warning" icon={<TriangleAlert size={22} />} title={t('secretary.outOfScope.title')}>
          {t(`secretary.outOfScope.topic.${topic}`)}
        </Verdict>
      );
    }

    case 'NOT_FOUND': {
      const name = result.detail;
      const title = !name ? t('secretary.notFound.titleGeneric')
        : result.reason === 'not_booked_today' ? t('secretary.notFound.notBookedToday', { name })
          : result.reason === 'no_such_patient' ? t('secretary.notFound.noSuchPatient', { name })
            : t('secretary.notFound.title', { name });
      const hint = result.reason === 'no_doctor' ? t('secretary.notFound.noDoctor')
        : result.reason === 'not_booked_today' ? t('secretary.notFound.notBookedTodayHint')
          : t('secretary.notFound.hint');
      return (
        <div data-testid="secretary-not-found">
          <Verdict icon={<CircleHelp size={22} />} title={title}>{hint}</Verdict>
          {/* The closest names that were checked — «پیدا نکردم» shows its work, and a
              misheard name is still one press from the right file. */}
          {result.patients?.length ? (
            <div style={{ marginTop: 18 }}>
              <PatientChoice compact title={t('secretary.choosePatient.closestTitle')} patients={result.patients} onPick={session.choosePatient} tr={tr} />
            </div>
          ) : null}
        </div>
      );
    }

    default:
      break;
  }

  const nothingHeard = result.reason === 'nothing_heard';
  return (
    <div data-testid="secretary-unknown">
      <Verdict
        icon={<CircleHelp size={22} />}
        title={nothingHeard ? t('secretary.unknown.nothingHeardTitle') : t('secretary.unknown.title')}
      >
        {nothingHeard ? t('secretary.unknown.nothingHeardHint') : t('secretary.unknown.hint')}
      </Verdict>
      <div style={S.row}>
        <Button type="button" autoFocus onClick={session.again} aria-keyshortcuts="F1" data-testid="secretary-unknown-again">
          {t('secretary.heard.again')} <KeyCap combo="f1" />
        </Button>
      </div>
    </div>
  );
}

function CostLine({ session, t, formatNumber }) {
  const command = session.priceOf?.(PRICE_COMMAND);
  const minute = session.priceOf?.(PRICE_TRANSCRIPTION);
  const known = Number.isFinite(command) && Number.isFinite(minute);
  let text;
  if (session.phase === PHASE.LISTENING) {
    text = known
      ? t('secretary.cost.before', { minute: formatNumber(minute), command: formatNumber(command) })
      : t('secretary.cost.beforeUnknown');
  } else if (session.phase === PHASE.PROCESSING) {
    text = t('secretary.cost.processing');
  } else if (session.phase === PHASE.RESULT) {
    if (session.result?.charged) {
      text = Number.isFinite(command) ? t('secretary.cost.charged', { command: formatNumber(command) }) : t('secretary.cost.chargedUnknown');
    } else {
      text = session.result?.reason === 'nothing_heard' ? t('secretary.cost.free') : t('secretary.cost.notCharged');
    }
    if (session.question) text = `${text} ${t('secretary.cost.answers')}`;
  } else {
    text = t('secretary.cost.free');
  }
  return <span data-testid="secretary-cost">{text}</span>;
}

/** The status under the window's title. */
function statusOf(phase, t) {
  if (phase === PHASE.LISTENING) return t('secretary.status.listening');
  if (phase === PHASE.PROCESSING) return t('secretary.status.processing');
  if (phase === PHASE.RESULT) return t('secretary.status.ready');
  return t('secretary.status.stopped');
}

/**
 * One result's window (PM-0080). Opened from its notification, and only when
 * something has to be answered or chosen first (model.routeOf): a centred dialog
 * on a desk, a sheet on a phone (AdaptiveModal). Closing it keeps the result —
 * the notification comes back as it was left.
 *
 *   item            { id, text, result, error, selections, skipped, answered, facts }
 *   onUpdate(patch) the session settled something; the item keeps it
 *   onBook(handoff) open «ثبت نوبت» prefilled (model.bookingHandoff)
 *   onDone(message) a write the window committed itself succeeded
 *   onOpenPatient(id), onDismiss() «انصراف» — this result is not wanted,
 *   onRecordAgain() «دوباره بگویید» — a new command, recorded by the record button
 */
export default function SecretaryWindow({
  item, onClose, onUpdate, onBook, onDone, onOpenPatient, onDismiss, onRecordAgain,
}) {
  const tr = useTranslation();
  const { t, formatDigits, formatNumber } = tr;
  const session = useSecretary({ initial: item, onChange: onUpdate, onRecordAgain });
  const { phase, result } = session;
  // «شارژ» on the out-of-credit view only where credit is sold (hooks/useCreditPurchase.js).
  const topUp = useCreditPurchase(phase === PHASE.NO_CREDIT);
  // «بیمار دیگری است؟» — open in the window until the next answer arrives.
  const [choosing, setChoosing] = React.useState(false);
  React.useEffect(() => { setChoosing(false); }, [result]);
  const others = result ? otherCandidates(result) : [];
  // Not for a NEW patient: the proposal's own notice offers the closest names.
  const canSwitch = phase === PHASE.RESULT && result?.outcome !== 'CHOOSE_PATIENT'
    && others.length > 0 && !resultPatient(result)?.isNew;

  // «بیمار دیگری است؟» takes the window: a question's recording stops, unsent.
  const openChoosing = (next) => {
    if (next) session.cancelAnswer();
    setChoosing(next);
  };

  // A result that no longer needs this window goes where it belongs — once: the
  // last question answered or skipped, a patient chosen, a sentence corrected.
  const routes = React.useRef({ onBook, onOpenPatient });
  routes.current = { onBook, onOpenPatient };
  const routed = React.useRef(null);
  // What the window OPENED on stays in it: only what happens here moves it on.
  const opened = React.useRef({ result: item?.result || null, skipped: (item?.skipped || []).length });
  React.useEffect(() => {
    if (phase !== PHASE.RESULT || !result || routed.current === result) return;
    if (result === opened.current.result && session.skipped.size === opened.current.skipped) return;
    const route = routeOf(result, session.skipped);
    if (route === 'window') return;
    routed.current = result;
    if (route === 'form') routes.current.onBook(bookingHandoff(result.booking, 0));
    else if (route === 'formDay') routes.current.onBook(bookingHandoff(result.booking, -1));
    else if (route === 'navigate') routes.current.onOpenPatient(result.patient.id);
  }, [phase, result, session.skipped]);

  // F1 inside the window: the one thing the microphone can do next — end what it
  // is recording, or start a new command.
  const talk = phase === PHASE.LISTENING ? session.finish
    : session.answer?.stage === 'listening' ? session.finishAnswer
      : (phase === PHASE.RESULT || phase === PHASE.ERROR || phase === PHASE.NO_MIC) ? session.again : null;
  useShortcut('f1', () => talk?.(), {
    label: t('secretary.shortcuts.talk'), group: t('secretary.shortcuts.open'), enabled: Boolean(talk),
  });
  // Registered as well as handled by the dialog itself, so it takes precedence over
  // an `esc` the screen underneath has bound for exactly as long as this is mounted.
  useShortcut('esc', onClose, { label: t('secretary.close'), group: t('secretary.shortcuts.open') });
  // «ادامه بگویید» — record more of the same command; it is appended to what was heard.
  useShortcut('shift+f1', () => session.continueSpeaking(), {
    label: t('secretary.shortcuts.continue'), group: t('secretary.shortcuts.open'),
    enabled: phase === PHASE.RESULT && Boolean(session.text),
  });

  const micProblem = session.micStatus === 'denied' ? t('secretary.noMic.denied')
    : session.micStatus === 'unsupported' ? t('secretary.noMic.unsupported') : t('secretary.noMic.generic');
  const small = result?.kind === 'mark_arrived' || result?.kind === 'patient_balance';

  let body = null;
  if (phase === PHASE.LISTENING) {
    // «ادامه بگویید» — the only recording that starts inside a window.
    body = (
      <div style={{ textAlign: 'center', padding: '8px 0' }} data-testid="secretary-listening">
        <div
          aria-hidden="true"
          style={{
            width: 72, height: 72, margin: '0 auto', borderRadius: 999, display: 'grid', placeItems: 'center',
            background: GRADIENT_BRAND, color: C.card, boxShadow: `0 0 0 10px ${soft(C.navy, 0.08)}`,
          }}
        >
          <Mic size={30} strokeWidth={1.6} />
        </div>
        <div style={{ marginTop: 22, fontSize: 22, fontWeight: 800, color: C.textPrimary }}>{t('secretary.listen.continueTitle')}</div>
        <div dir="auto" style={{ maxWidth: 440, margin: '4px auto 0', fontSize: 13.5, color: C.textSecondary }} data-testid="secretary-so-far">
          {t('secretary.listen.soFar', { text: session.text })}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '12px 0 4px', fontSize: 16, fontWeight: 700 }}>
          <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 999, background: session.recording ? C.red : C.border }} />
          <span dir="ltr" style={S.ltr} data-testid="secretary-clock">{formatDigits(clockOf(session.seconds))}</span>
        </div>
        <div style={{ fontSize: 13.5, color: C.textSecondary }}>
          {session.recording ? t('secretary.listen.sub') : t('secretary.listen.preparing')}
        </div>
        <div dir="auto" aria-live="polite" style={{ minHeight: 22, margin: '6px 0 12px', fontSize: 14, color: C.textSecondary }} data-testid="secretary-interim">
          {session.interim}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
          <Button type="button" autoFocus onClick={session.finish} disabled={!session.recording} aria-keyshortcuts="F1" data-testid="secretary-stop">
            <Square size={14} aria-hidden="true" fill="currentColor" /> {t('secretary.listen.stop')} <KeyCap combo="f1" />
          </Button>
          <Button type="button" variant="ghost" onClick={session.cancelContinue} data-testid="secretary-continue-cancel">{t('secretary.listen.backToResult')}</Button>
        </div>
      </div>
    );
  } else if (phase === PHASE.PROCESSING) {
    body = (
      <SkeletonRegion label={t('secretary.status.processing')} data-testid="secretary-processing">
        <div style={{ ...S.card, padding: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8, color: C.textPrimary }}>{t('secretary.processing.title')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 14 }}>
            <Check size={18} aria-hidden="true" color={C.green} /> {t('secretary.processing.received')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 14, fontWeight: 700 }}>
            <Clock size={18} aria-hidden="true" color={C.navy} /> {t('secretary.processing.understanding')}
          </div>
          <Skeleton height={44} radius={10} style={{ marginTop: 10 }} />
        </div>
      </SkeletonRegion>
    );
  } else if (phase === PHASE.NO_CREDIT) {
    body = (
      <div data-testid="secretary-no-credit">
        <Alert variant="warning" title={t('secretary.noCredit.title')}>
          {session.creditAfterRun ? t('secretary.noCredit.afterRun') : t('secretary.noCredit.notStarted')}
        </Alert>
        {topUp && (
          <div style={{ ...S.row, justifyContent: 'flex-start' }}>
            <Button asChild>
              <a href={accountUrl('/credits')} data-testid="secretary-top-up">{t('secretary.noCredit.topUp')}</a>
            </Button>
          </div>
        )}
        <p style={{ ...S.hint, margin: '14px 0 0' }}>{t('secretary.noCredit.manual')}</p>
      </div>
    );
  } else if (phase === PHASE.NO_MIC) {
    body = (
      <div data-testid="secretary-no-mic">
        <Verdict icon={<MicOff size={22} />} title={t('secretary.noMic.title')}>{micProblem}</Verdict>
        <div style={{ ...S.row, justifyContent: 'flex-start' }}>
          <Button type="button" autoFocus onClick={session.continueSpeaking} data-testid="secretary-mic-retry">{t('secretary.noMic.retry')}</Button>
        </div>
      </div>
    );
  } else if (phase === PHASE.ERROR) {
    body = (
      <div data-testid="secretary-error">
        {session.text ? <HeardCard text={session.text} onAgain={session.again} onRunEdited={session.runEdited} t={t} /> : null}
        <Alert variant="error" title={t('secretary.error.title')}>{t('secretary.error.hint')}</Alert>
        <div style={{ ...S.row, justifyContent: 'flex-start' }}>
          {session.text ? <Button type="button" autoFocus onClick={session.retry} data-testid="secretary-retry">{t('secretary.error.retry')}</Button> : null}
          <Button type="button" variant="outline" onClick={session.again} data-testid="secretary-error-again">{t('secretary.error.again')}</Button>
        </div>
      </div>
    );
  } else if (result) {
    body = (
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        data-testid="secretary-result"
        data-outcome={result.outcome}
      >
        <HeardCard
          text={session.text}
          onAgain={session.again}
          onContinue={session.continueSpeaking}
          onRunEdited={session.runEdited}
          onEdit={session.cancelAnswer}
          t={t}
        />
        {/* What the sentence left out, above what was prepared — not over «بیمار دیگری است؟». */}
        {!choosing && session.question ? <QuestionCard session={session} tr={tr} /> : null}
        <section style={{ ...S.card, padding: 16, minWidth: 0, boxSizing: 'border-box' }}>
          <Prepared
            session={session}
            choosing={choosing}
            onChoosing={openChoosing}
            onBook={onBook}
            onDone={onDone}
            onOpenPatient={onOpenPatient}
            onClose={onDismiss}
            tr={tr}
          />
        </section>
        <UnderstoodCard
          result={result}
          onOtherPatient={canSwitch && !choosing ? () => openChoosing(true) : null}
          t={t}
          formatDate={tr.formatDate}
          formatTime={tr.formatTime}
          formatDigits={formatDigits}
        />
      </div>
    );
  }

  return (
    <AdaptiveModal
      open
      onClose={onClose}
      title={t('secretary.name')}
      subtitle={<span aria-live="polite" data-testid="secretary-status">{statusOf(phase, t)}</span>}
      size={small ? 'sm' : 'md'}
      snap={0.92}
      footer={(
        <div style={{ fontSize: 12, color: C.textSecondary, textAlign: 'center', lineHeight: 1.7, paddingTop: 10 }}>
          <CostLine session={session} t={t} formatNumber={formatNumber} />
        </div>
      )}
      data-testid="secretary-window"
    >
      <div data-state={phase} data-testid="secretary-window-body">{body}</div>
    </AdaptiveModal>
  );
}
